---
title: "How to remove line breaks from text copied from a PDF"
description: "Join hard-wrapped PDF lines back into paragraphs, preserve blank-line boundaries, and avoid guessing at words split with hyphens."
locale: "en"
date: "2026-08-03"
tags: ["text", "pdf", "line breaks", "cleanup"]
relatedTools: ["textCleanup", "textCounter"]
translations:
  ru: "ubrat-perenosy-strok-posle-kopirovaniya-iz-pdf"
faq:
  - q: "Can I join wrapped lines while keeping paragraph boundaries?"
    a: "Yes. The Join wrapped lines rule combines non-empty lines inside a paragraph, while a blank line remains the paragraph boundary."
  - q: "Does the tool repair words split with a hyphen across lines?"
    a: "No. Hyphens and punctuation are not changed automatically because a tool without a dictionary cannot reliably distinguish a split word from a real hyphen."
  - q: "Is text copied from the PDF sent to a server?"
    a: "No. Cleanup and preview run locally in the browser; the text content is neither sent nor stored."
---

A paragraph copied from a PDF lands in your editor as 18 short lines. Each one
ends where the page column ended, even though the sentence continues. Deleting
17 breaks by hand is possible. By page five, it is a bad plan.

Open [Clean text](/ochistit-tekst), paste the fragment, and enable "Join wrapped
lines". Non-empty lines inside one paragraph are joined with spaces. A blank
line stays as the boundary between paragraphs. The result updates immediately,
so you can compare the source and cleaned versions before copying anything.

## Why plain-text paste does not fix it

A PDF describes a fixed page layout. Its text layer knows where characters and
lines sit on the page, but it does not always know whether a line ended because
the column was full or because the author started a new paragraph. The
clipboard carries those boundaries with the text.

"Paste as plain text" removes font, color, and size, but the newline characters
usually survive. Replacing every newline with a space is too blunt as well. A
heading, a quotation, and the next paragraph can collapse into one block.

Txtify uses a narrower rule: adjacent non-empty lines belong to one paragraph,
and a blank line starts the next one. Even if you also enable "Remove blank
lines", neighboring paragraphs are not silently merged. They remain separate
output lines.

## Check hyphens by eye

The lines `docu-` and `ment` may represent the word `document`. The lines
`well-` and `known` may need to stay `well-known`. Deleting a hyphen automatically is risky,
so txtify does not change hyphens, punctuation, or Unicode normalization.

After joining the lines, inspect hyphens that appeared at line ends in the
source and compare them with the PDF. Headers, page numbers, and text copied
from a neighboring column need the same manual check. This tool cleans line
boundaries; it does not reconstruct the page structure.

Repeated regular spaces can be normalized with a separate switch. Tabs can be
replaced with exactly two ASCII spaces, while non-breaking spaces stay intact.
One operation is limited to 200,000 Unicode code points and 20,000 lines. The
text stays in the tab's memory and is not uploaded.

If length matters after cleanup, use the
[word and character counter](/schetchik-simvolov). The
[local text tools map](/blog/local-text-tools-in-browser) covers the other
short browser-only operations.
